// src/components/layouts/MainLayout.js
import React from "react";
import TopAppBar from "../navigation/TopAppBar";
import BottomNav from "../navigation/BottomNav";
import { APP_SHELL_LAYOUT } from "../../constants/constants";
import { Box, Container } from "@mui/material";

const DEFAULT_MAIN_MAX_WIDTH = { xs: "100%", md: "75%", lg: "60%" };
const DENSITY_TO_PADDING = {
  compact: { xs: 0, sm: 0 },
  comfortable: { xs: 0, sm: 2 },
};

const MainLayout = ({
  children,
  showTopAppBar = true,
  showBottomNav = true,
  mainMaxWidth = DEFAULT_MAIN_MAX_WIDTH,
  contentDensity = "compact",
  disableMainScroll = false,
}) => {
  const mainPaddingX = DENSITY_TO_PADDING[contentDensity] || DENSITY_TO_PADDING.compact;

  React.useLayoutEffect(() => {
    if (!disableMainScroll || typeof document === "undefined") return;
    const mainScrollContainer = document.getElementById("main-layout-scroll-container");
    if (mainScrollContainer) {
      mainScrollContainer.scrollTop = 0;
      mainScrollContainer.scrollLeft = 0;
    }
  }, [disableMainScroll]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        backgroundColor: "background.default",
        color: "text.primary",
        height: "100vh",
        overflow: "hidden",
        "@supports (height: 100dvh)": {
          height: "100dvh",
        },
      }}
    >
      {showTopAppBar ? <TopAppBar /> : null}

      <Container
        component="main"
        id="main-layout-scroll-container"
        sx={{
          flexGrow: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          width: mainMaxWidth,
          mx: { xs: 0, md: "auto" },
          mt: showTopAppBar ? APP_SHELL_LAYOUT.TOP_APP_BAR_OFFSET_CSS : 0,
          px: mainPaddingX,
          pt: 0,
          pb: 0,
          overflowY: disableMainScroll ? "hidden" : "auto",
          overflowX: "hidden",
          overscrollBehaviorY: disableMainScroll ? "none" : "auto",
          overflowAnchor: "none",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {/* The bottom nav is fixed on top of the page, so its height has to be
            reserved somewhere. It lives on this wrapper rather than as padding on
            the scroll container above: a scroller's own bottom padding is not
            reliably part of its scrollable area on mobile WebKit, which drops the
            reservation exactly when the page is long enough to scroll — the case
            that needs it. Padding on an ordinary in-flow element always counts.

            `flex: 1 0 auto` keeps this identical to the old padding for every
            other case: the wrapper still grows to fill a short page (so
            `minHeight: 100%` backgrounds and `flex: 1` children stretch as
            before) and never shrinks below its content on a long one. */}
        <Box
          sx={{
            flex: "1 0 auto",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            pb: showBottomNav ? APP_SHELL_LAYOUT.BOTTOM_NAV_OFFSET_CSS : 0,
          }}
        >
          {children}
        </Box>
      </Container>

      {showBottomNav ? <BottomNav /> : null}
    </Box>
  );
};

export default MainLayout;
